/* src/components/customGame/setup/panels/ControlsPanel.js */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Re-using the SettingsSection helper
const SettingsSection = ({ title, children, helpText = "", className = "" }) => (
    <div className={`mb-6 p-4 border border-gray-200 rounded-md shadow-sm bg-white ${className}`}>
        <h3 className="text-lg font-medium text-gray-800 mb-3">{title}</h3>
        {helpText && <p className="text-sm text-gray-500 mb-3 -mt-2">{helpText}</p>}
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

SettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
    className: PropTypes.string,
};

const INPUT_METHODS_AVAILABLE = [
    { key: 'Keyboard', label: 'Keyboard' },
    { key: 'Mouse Click', label: 'Mouse Click (On-screen Buttons)' },
    { key: 'Touch', label: 'Touch (On-screen Buttons)' },
];

// This would ideally come from globalStimulusConfig.EnabledTypes or similar
// For now, a static list for demonstration of key mapping UI
const MAPPABLE_ACTIONS = [
    { key: 'Pause', label: 'Pause/Resume Game' },
    // Dynamically add actions based on enabled stimuli later
    // e.g., { key: 'AudioMatch', label: 'Audio Match' }, { key: 'PositionMatch', label: 'Position Match' }
];


const KeyCaptureInput = ({ value, onChange, actionLabel }) => {
    const [isCapturing, setIsCapturing] = useState(false);

    const handleKeyDown = (event) => {
        if (isCapturing) {
            event.preventDefault();
            event.stopPropagation();
            // Basic key representation (can be enhanced for Shift+, Ctrl+, etc.)
            let keyValue = event.key;
            if (event.key === " ") keyValue = "Space"; // Friendlier name for space
            else if (event.key.length === 1) keyValue = keyValue.toUpperCase(); // Uppercase single letters

            if (event.ctrlKey && keyValue !== "Control") keyValue = `Ctrl+${keyValue}`;
            if (event.altKey && keyValue !== "Alt") keyValue = `Alt+${keyValue}`;
            if (event.shiftKey && keyValue !== "Shift") keyValue = `Shift+${keyValue}`;
            // Meta key (Cmd on Mac, Win on Windows)
            if (event.metaKey && keyValue !== "Meta") keyValue = `Meta+${keyValue}`;


            onChange(keyValue);
            setIsCapturing(false);
        }
    };

    const startCapture = () => {
        setIsCapturing(true);
        // Optionally focus the button or a hidden input to ensure key events are caught
    };

    useEffect(() => {
        if (isCapturing) {
            // Add global listener when capturing, remove on cleanup or capture
            document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
            return () => {
                document.removeEventListener('keydown', handleKeyDown, true);
            };
        }
    }, [isCapturing, onChange]); // handleKeyDown dependency removed as it now uses onChange from closure

    return (
        <div className="flex items-center space-x-2">
            <input
                type="text"
                readOnly
                value={value || ''}
                placeholder="Press a key"
                className="flex-grow p-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                onClick={startCapture} // Also allow click to start capture
            />
            <button
                type="button"
                onClick={startCapture}
                onBlur={() => setIsCapturing(false)} // Stop capturing if button loses focus
                className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                    isCapturing 
                        ? 'bg-indigo-600 text-white border-indigo-600 ring-2 ring-indigo-300' 
                        : 'bg-gray-200 hover:bg-gray-300 border-gray-300 text-gray-700'
                }`}
            >
                {isCapturing ? 'Capturing...' : 'Set Key'}
            </button>
        </div>
    );
};

KeyCaptureInput.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    actionLabel: PropTypes.string.isRequired,
};


const ControlsPanel = ({ config, onConfigChange, pathPrefix, globalStimulusConfig }) => {
    const currentConfig = config || {};
    const keyMappingConfig = currentConfig.KeyMapping || {};

    const handleChange = (key, value) => {
        onConfigChange(`${pathPrefix}.${key}`, value);
    };

    const handleKeyMappingChange = (subKey, value) => {
        handleChange(`KeyMapping.${subKey}`, value);
    };
    
    const handleCustomKeyChange = (actionKey, keyValue) => {
        const newCustomMappings = { ...(keyMappingConfig.Custom || {}), [actionKey]: keyValue };
        handleKeyMappingChange('Custom', newCustomMappings);
    };

    const handleInputMethodChange = (methodKey, isChecked) => {
        const currentMethods = currentConfig.InputMethods || [];
        let newMethods;
        if (isChecked) {
            newMethods = [...currentMethods, methodKey];
        } else {
            newMethods = currentMethods.filter(item => item !== methodKey);
        }
        handleChange('InputMethods', newMethods);

        // If Mouse/Touch are disabled, consider hiding on-screen buttons
        if (methodKey === 'Mouse Click' || methodKey === 'Touch') {
            if (!newMethods.includes('Mouse Click') && !newMethods.includes('Touch')) {
                handleChange('ShowOnScreenButtons', false);
            } else {
                 handleChange('ShowOnScreenButtons', true); // Show if at least one is enabled
            }
        }
    };
    
    // Dynamically determine mappable actions based on enabled stimuli
    // This is a crucial part that connects to globalStimulusConfig
    const getDynamicMappableActions = () => {
        const actions = [{ key: 'Pause', label: 'Pause/Resume Game' }];
        const enabledStimuli = globalStimulusConfig?.EnabledTypes || [];

        enabledStimuli.forEach(stimulusType => {
            // Create a user-friendly label
            let label = stimulusType.charAt(0).toUpperCase() + stimulusType.slice(1);
            if (stimulusType === 'Position') label = 'Position/Visual'; // Common terminology
            
            actions.push({ key: `${stimulusType}Match`, label: `${label} Match` });
        });
        return actions;
    };
    const dynamicMappableActions = getDynamicMappableActions();


    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-full">
            <SettingsSection title="Input Methods" helpText="How users can respond during the game.">
                <div className="space-y-2">
                    {INPUT_METHODS_AVAILABLE.map(method => (
                        <div key={method.key} className="flex items-center">
                            <input
                                id={`inputMethod-${method.key.replace(/\s+/g, '-')}`}
                                type="checkbox"
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                checked={(currentConfig.InputMethods || []).includes(method.key)}
                                onChange={(e) => handleInputMethodChange(method.key, e.target.checked)}
                            />
                            <label htmlFor={`inputMethod-${method.key.replace(/\s+/g, '-')}`} className="ml-2 block text-sm font-medium text-gray-700">
                                {method.label}
                            </label>
                        </div>
                    ))}
                </div>

                {((currentConfig.InputMethods || []).includes('Mouse Click') || (currentConfig.InputMethods || []).includes('Touch')) && (
                    <div className="flex items-center mt-3 pl-1">
                        <input
                            id="showOnScreenButtons"
                            type="checkbox"
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            checked={currentConfig.ShowOnScreenButtons !== undefined ? currentConfig.ShowOnScreenButtons : true}
                            onChange={(e) => handleChange('ShowOnScreenButtons', e.target.checked)}
                        />
                        <label htmlFor="showOnScreenButtons" className="ml-2 block text-sm font-medium text-gray-700">
                            Show On-Screen Match Buttons
                        </label>
                    </div>
                )}
            </SettingsSection>

            <SettingsSection title="Keyboard Hotkey Mapping" helpText="Customize keyboard shortcuts for game actions.">
                <div>
                    <label htmlFor="keyMappingPreset" className="block text-sm font-medium text-gray-700 mb-1">
                        Key Mapping Preset
                    </label>
                    <select
                        id="keyMappingPreset"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={keyMappingConfig.Preset || 'Default (A=Visual/Position, L=Audio)'}
                        onChange={(e) => handleKeyMappingChange('Preset', e.target.value)}
                    >
                        <option value="Default (A=Visual/Position, L=Audio)">Default (A=Visual/Position, L=Audio)</option>
                        <option value="Custom">Custom Mapping</option>
                    </select>
                </div>

                {keyMappingConfig.Preset === 'Custom' && (
                    <div className="mt-4 space-y-3">
                        <p className="text-sm text-gray-600">Define custom keys for each action. Click 'Set Key' then press the desired key or combination.</p>
                        {dynamicMappableActions.map(action => (
                            <div key={action.key}>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {action.label}:
                                </label>
                                <KeyCaptureInput
                                    actionLabel={action.label}
                                    value={(keyMappingConfig.Custom || {})[action.key]}
                                    onChange={(keyValue) => handleCustomKeyChange(action.key, keyValue)}
                                />
                            </div>
                        ))}
                        <p className="text-xs text-gray-500 mt-2">
                            Supported modifiers: Ctrl, Alt, Shift, Meta (Cmd/Win). E.g., "Ctrl+S", "Shift+A".
                            Single keys like "A", "L", "Space" are also supported.
                        </p>
                    </div>
                )}
            </SettingsSection>
        </div>
    );
};

ControlsPanel.propTypes = {
    config: PropTypes.shape({
        InputMethods: PropTypes.arrayOf(PropTypes.string),
        ShowOnScreenButtons: PropTypes.bool,
        KeyMapping: PropTypes.shape({
            Preset: PropTypes.string,
            Custom: PropTypes.object, // e.g., { AudioMatch: "L", PositionMatch: "A" }
        }),
    }).isRequired,
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Controls"
    globalStimulusConfig: PropTypes.object, // To get EnabledTypes for dynamic key mapping
};

export default ControlsPanel;